import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  RefreshCw,
  X,
  Pencil,
  Copy,
  Archive,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Database,
  Layers,
  ArrowRight,
  Calculator,
  LayoutTemplate,
  UsersRound,
  ListPlus,
  Lightbulb,
  Zap,
  TrendingDown,
  Info,
  ChevronLeft,
} from "lucide-react";
import { v2CreateComponent, v2ListComponents, v2SeedDefaults, v2UpdateComponent } from "../SalaryApiV2";
import API from "../../../../services/api";
import { entBulkSeed } from "../salaryEnterpriseApi";
import { useToast } from "../../../Context/ToastContext";
import { useSetupProgress } from "../../../Context/SetupProgressContext";
import { markSalaryComponentsConfigured } from "../../../../services/markSalaryComponentsConfigured";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import ErrorAlert from "../../../../components/ui/ErrorAlert";
import { classifyApiError } from "../../../../utils/normalizeApiError";
import PayrollSetupProgress from "./components/PayrollSetupProgress";
import {
  categoryMeta,
  calcMeta,
  displayCategory,
  formulaStatus,
  buildWarnings,
  buildPayload,
  rowToForm,
  isDeletable,
  isEarningSection,
  isDeductionSection,
  isVariablePayrollComponent,
  isOvertimePayrollComponent,
  overlayOvertimeFromExtraWork,
  variableComponentMeta,
  componentCodeOf,
} from "./components/salaryComponentModel";
import {
  appendWizardSelectedComponentIds,
  writeWizardSelectedComponentIds,
  repairWizardComponentSelection,
} from "../wizard/shared";
import { PAYROLL_SETUP_TABS } from "../payrollSetupNav";
import {
  readAdvancedModeEnabled,
  writeAdvancedModeEnabled,
  startSalaryTemplatePipeline,
  pipelineStepPath,
  PIPELINE_STEPS,
} from "./components/salaryTemplatePipeline";
import "../../../../styles/salary-components-premium.css";
import "../../../../styles/salary-template-pipeline.css";

/* ── tiny presentational helpers ───────────────────────────── */
function CategoryBadge({ category }) {
  const m = categoryMeta(category);
  return <span className={`bw-badge ${m.badge}`}>{m.label}</span>;
}
function CalcBadge({ calc, row }) {
  const m = calcMeta(calc, row);
  return <span className={`scp-pill ${m.badge}`}>{m.label}</span>;
}
function FormulaBadge({ row }) {
  const s = formulaStatus(row);
  if (s.tone === "assigned") return <span className="scp-pill scp-status-ok"><CheckCircle2 size={12} /> {s.label}</span>;
  if (s.tone === "missing") return <span className="scp-pill scp-status-warn"><AlertTriangle size={12} /> {s.label}</span>;
  return <span className="scp-pill scp-status-muted">{s.label}</span>;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

/* ── main page ─────────────────────────────────────────────── */
export default function SalaryComponentsHub() {
  const toast = useToast();
  const navigate = useNavigate();
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};
  const defaultsSeededRef = useRef(false);

  async function acknowledgeComponentsConfigured() {
    await markSalaryComponentsConfigured();
    await refetchSetupProgress?.();
  }

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);
  const [seeding, setSeeding] = useState(false);

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [formulaFilter, setFormulaFilter] = useState("all"); // all | required | missing

  const [sort, setSort] = useState({ key: "code", dir: "asc" });

  // selection
  const [selected, setSelected] = useState(() => new Set());

  const [nextLoading, setNextLoading] = useState(false);
  const [setupRefreshKey, setSetupRefreshKey] = useState(0);
  const [advancedMode, setAdvancedMode] = useState(() => readAdvancedModeEnabled());
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef(null);

  useEffect(() => {
    if (!guideOpen) return undefined;
    function onDown(e) {
      if (guideRef.current && !guideRef.current.contains(e.target)) setGuideOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setGuideOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [guideOpen]);

  // confirms
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [saveReviewOpen, setSaveReviewOpen] = useState(false);

  function showError(e, fallback) {
    const info = classifyApiError(e, fallback);
    setErrorInfo(info);
    toast.error(info.message);
  }

  async function load() {
    try {
      setLoading(true);
      setErrorInfo(null);
      const [data, cfgRes] = await Promise.all([
        v2ListComponents(),
        API.get("/organisation/me/extra-work-config").catch(() => null),
      ]);
      const extra = cfgRes?.data?.extra_work || null;
      let list = Array.isArray(data) ? data : [];
      // If seed did not run yet, retry once (backend also auto-seeds on list when incomplete).
      const starterCodes = new Set(["BASIC", "HRA", "SPA", "SPECIAL", "PF", "PT", "TDS"]);
      const hasStarterFormulas = list.some(
        (r) => r.is_active !== false && starterCodes.has(componentCodeOf(r)),
      );
      if (!hasStarterFormulas) {
        try {
          await v2SeedDefaults();
          const retry = await v2ListComponents();
          list = Array.isArray(retry) ? retry : list;
        } catch {
          /* load still proceeds */
        }
      }

      const special = list.filter(isVariablePayrollComponent);
      if (special.length) {
        appendWizardSelectedComponentIds(special.map((r) => r.component_id));
      }
      repairWizardComponentSelection(list);

      const hasOvertime = list.some((r) => componentCodeOf(r) === "OVERTIME" && r.is_active !== false);
      const withoutDuplicateOt = list.filter((r) => !(hasOvertime && componentCodeOf(r) === "OT"));
      const synced = withoutDuplicateOt.map((r) => overlayOvertimeFromExtraWork(r, extra));
      // Hub shows the full starter + configured set (not the old wizard subset of 2 variable rows).
      setRows(
        synced.filter(
          (r) =>
            isVariablePayrollComponent(r) ||
            (r.is_active !== false && formulaStatus(r)?.tone !== "missing"),
        ),
      );
      setSelected(new Set());
      setSetupRefreshKey((k) => k + 1);
    } catch (e) {
      showError(e, "Failed to load components");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // load is recreated each render; this should run once when the hub mounts.
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (!defaultsSeededRef.current) {
        defaultsSeededRef.current = true;
        try {
          await v2SeedDefaults();
        } catch {
          /* non-blocking — list load still runs */
        }
      }
      if (!cancelled) await load();
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const warnings = useMemo(() => buildWarnings(rows), [rows]);

  const hasAnyComponents = rows.some((r) => r.is_active !== false);
  const overtimeRow = rows.find((r) => componentCodeOf(r) === "OVERTIME" && r.is_active !== false);
  const advanceRow = rows.find((r) => componentCodeOf(r) === "ADVANCE_TAKEN" && r.is_active !== false);
  const earningsConfigured = rows.some((r) => r.is_active !== false && isEarningSection(r));
  const deductionsConfigured = rows.some((r) => r.is_active !== false && isDeductionSection(r));
  const componentsReady = earningsConfigured && deductionsConfigured && Boolean(overtimeRow) && Boolean(advanceRow) && warnings.length === 0;
  const setupTabIndex = PAYROLL_SETUP_TABS.findIndex((t) => t.id === "components");
  const prevSetupTab = setupTabIndex > 0 ? PAYROLL_SETUP_TABS[setupTabIndex - 1] : null;
  const nextSetupTab = setupTabIndex >= 0 && setupTabIndex < PAYROLL_SETUP_TABS.length - 1 ? PAYROLL_SETUP_TABS[setupTabIndex + 1] : null;

  /* derived, filtered + sorted list */
  const view = useMemo(() => {
    let list = rows.map((r) => ({ ...r, _cat: displayCategory(r), _fs: formulaStatus(r) }));

    if (statusFilter === "active") {
      list = list.filter((r) => r.is_active !== false || isVariablePayrollComponent(r));
    } else if (statusFilter === "inactive") list = list.filter((r) => r.is_active === false);

    if (formulaFilter === "required") list = list.filter((r) => !isVariablePayrollComponent(r) && (r._fs.requiresFormula || r.calculation_type === "system"));
    else if (formulaFilter === "missing") list = list.filter((r) => r._fs.tone === "missing");

    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => `${r.code} ${r.name} ${r.description || ""}`.toLowerCase().includes(q));

    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av;
      let bv;
      switch (sort.key) {
        case "name": av = a.name || ""; bv = b.name || ""; break;
        case "category": av = a._cat; bv = b._cat; break;
        case "calc": av = a.calculation_type || ""; bv = b.calculation_type || ""; break;
        case "updated": av = a.updated_at || ""; bv = b.updated_at || ""; break;
        default: av = a.code || ""; bv = b.code || "";
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [rows, statusFilter, formulaFilter, search, sort]);

  const earningsRows = useMemo(
    () => view.filter((r) => isEarningSection(r)),
    [view]
  );
  const deductionsRows = useMemo(
    () => view.filter((r) => isDeductionSection(r)),
    [view]
  );

  function toggleSort(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }
  function SortHead({ k, children, align }) {
    const active = sort.key === k;
    return (
      <th className={align === "right" ? "text-right" : ""}>
        <button type="button" className={`scp-sort ${active ? "active" : ""}`} onClick={() => toggleSort(k)}>
          {children}
          {active ? (sort.dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : <ArrowUpDown size={12} className="scp-sort-idle" />}
        </button>
      </th>
    );
  }

  function toggleRow(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function openCreateForCategory(category = "earning") {
    const q = category === "deduction" ? "?category=deduction" : "";
    navigate(`/salary-v2/components/new${q}`);
  }
  function openCreate() {
    openCreateForCategory("earning");
  }
  function openEdit(row) {
    if (!row?.component_id) return;
    navigate(`/salary-v2/components/${row.component_id}/edit`, { state: { row } });
  }

  function assignFormula(row) {
    if (!row) return;
    const next = { ...row };
    if (next.calculation_type !== "formula" && next.calculation_type !== "system") {
      next.calculation_type = "formula";
    }
    navigate(`/salary-v2/components/${row.component_id}/edit`, { state: { row: next } });
  }

  async function duplicate(row) {
    if (isVariablePayrollComponent(row)) {
      toast.error("Overtime and Advance Taken cannot be duplicated — they are system-defined.");
      return;
    }
    try {
      const form = rowToForm(row);
      let newCode = `${form.code}_COPY`;
      const existing = new Set(rows.map((r) => (r.code || "").toUpperCase()));
      let i = 2;
      while (existing.has(newCode.toUpperCase())) newCode = `${form.code}_COPY${i++}`;
      const payload = buildPayload({ ...form, code: newCode, name: `${form.name} (Copy)` });
      const created = await v2CreateComponent(payload);
      if (created?.component_id) {
        appendWizardSelectedComponentIds([created.component_id]);
      }
      toast.success(`Duplicated as ${newCode}.`);
      await acknowledgeComponentsConfigured();
      await load();
    } catch (e) {
      showError(e, "Failed to duplicate component");
    }
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    try {
      setArchiveLoading(true);
      await v2UpdateComponent(archiveTarget.component_id, { is_active: false });
      toast.success(
        componentCodeOf(archiveTarget) === "ADVANCE_TAKEN" ? "Advance Taken deactivated." : "Component archived."
      );
      setArchiveTarget(null);
      await load();
    } catch (e) {
      showError(e, "Failed to archive component");
    } finally {
      setArchiveLoading(false);
    }
  }

  async function restore(row) {
    try {
      await v2UpdateComponent(row.component_id, { is_active: true });
      if (row?.component_id) {
        appendWizardSelectedComponentIds([row.component_id]);
      }
      toast.success(componentCodeOf(row) === "ADVANCE_TAKEN" ? "Advance Taken activated." : "Component restored.");
      await load();
    } catch (e) {
      showError(e, "Failed to restore component");
    }
  }

  async function confirmBulkArchive() {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      setBulkLoading(true);
      const byId = new Map(rows.map((r) => [r.component_id, r]));
      let ok = 0;
      for (const id of ids) {
        const r = byId.get(id);
        if (r && isDeletable(r)) {
          // eslint-disable-next-line no-await-in-loop
          await v2UpdateComponent(id, { is_active: false });
          ok += 1;
        }
      }
      toast.success(`Archived ${ok} component${ok !== 1 ? "s" : ""}.`);
      setBulkConfirm(false);
      await load();
    } catch (e) {
      showError(e, "Bulk archive failed");
    } finally {
      setBulkLoading(false);
    }
  }

  async function seedCatalog() {
    try {
      setSeeding(true);
      const res = await entBulkSeed({
        categories: ["earning", "benefit", "deduction", "employer_contribution"],
        skip_existing: true,
      });
      // Explicit catalog load counts as a user choice — include active components in the page list.
      const data = await v2ListComponents();
      const list = Array.isArray(data) ? data : [];
      writeWizardSelectedComponentIds(
        list.filter((r) => r.is_active !== false).map((r) => r.component_id),
      );
      toast.success(`Loaded ${res.created} components (${res.skipped} skipped).`);
      await load();
    } catch (e) {
      showError(e, "Could not load default components");
    } finally {
      setSeeding(false);
    }
  }

  function openSaveReview() {
    if (warnings.length > 0) {
      toast.error("Resolve formula and statutory warnings before continuing.");
      document.getElementById("scp-warnings")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!hasAnyComponents) {
      toast.error("Add at least one earning and one deduction before continuing.");
      return;
    }
    setSaveReviewOpen(true);
  }

  async function confirmSaveAndContinue() {
    try {
      setNextLoading(true);
      await acknowledgeComponentsConfigured();
      setSaveReviewOpen(false);
      toast.success("Salary components saved. Continuing to Salary Structures…");
      navigate(nextSetupTab?.to || "/salary-v2/structures");
    } catch (e) {
      showError(e, "Could not save salary components setup");
    } finally {
      setNextLoading(false);
    }
  }

  const activeComponents = useMemo(
    () => rows.filter((r) => r.is_active !== false),
    [rows],
  );
  const activeEarnings = useMemo(
    () => activeComponents.filter((r) => isEarningSection(r)),
    [activeComponents],
  );
  const activeDeductions = useMemo(
    () => activeComponents.filter((r) => isDeductionSection(r)),
    [activeComponents],
  );

  const selectedCount = selected.size;

  const setupGuideSteps = [
    {
      n: 1,
      Icon: ListPlus,
      title: "Create your components",
      text: "Add earnings like Basic & HRA, deductions, and statutory items like PF, ESI or PT.",
      cta: "Add a component",
      onClick: openCreate,
    },
    {
      n: 2,
      Icon: Calculator,
      title: "Set how each is calculated",
      text: "Use fixed amounts or formulas (e.g. BASIC × 40%) in Formula Builder.",
      cta: "Open Formula Builder",
      onClick: () => navigate("/salary-v2/formula-builder"),
    },
    {
      n: 3,
      Icon: LayoutTemplate,
      title: "Build a salary structure",
      text: "Group existing components into a reusable salary structure.",
      cta: "Open structures",
      onClick: () => navigate("/salary/templates"),
    },
    {
      n: 4,
      Icon: UsersRound,
      title: "Assign it to employees",
      text: "Apply the template so payroll calculates automatically.",
      cta: "Open Structures",
      onClick: () => navigate("/employeesalarystructure"),
    },
  ];

  return (
    <div className="scp scp--hub">
      <div className="scp-hero">
        <div className="scp-header">
          <div className="scp-header-titles">
            <h1 className="scp-title">
              <span className="scp-title-icon"><Layers size={18} /></span>
              Salary Components
            </h1>
            <p className="scp-subtitle">Earnings, deductions, and reimbursements used in payroll runs.</p>
          </div>
          <div className="scp-header-actions">
            <button type="button" className="bw-btn bw-btn-secondary scp-btn-ghost" onClick={seedCatalog} disabled={seeding} title="Load Brixigo default salary components">
              <Database size={15} /> {seeding ? "Loading…" : "Get default components"}
            </button>
            <button type="button" className="bw-btn bw-btn-primary scp-add-btn" onClick={openCreate}>
              <Plus size={16} /> Add Component
            </button>
          </div>
        </div>
      </div>

      {errorInfo ? (
        <ErrorAlert title={errorInfo.title} message={errorInfo.message} hint={errorInfo.hint} onDismiss={() => setErrorInfo(null)} onRetry={load} />
      ) : null}

      <div className="scp-compact-toolbar scp-compact-toolbar--pills">
        <div className="scp-drop-wrap" ref={guideRef}>
          <button
            type="button"
            className={`scp-drop-trigger scp-pill-btn scp-pill-btn--motion${guideOpen ? " is-open" : ""}`}
            aria-expanded={guideOpen}
            onClick={() => setGuideOpen((v) => !v)}
          >
            <Lightbulb size={13} />
            <span className="scp-drop-trigger-label">How payroll setup works</span>
            <ChevronDown size={14} className="scp-drop-chevron" aria-hidden />
          </button>
          {guideOpen ? (
            <section className="scp-card scp-guide scp-guide-dropdown">
              <p className="scp-guide-sub scp-guide-dropdown-intro">
                Follow these 4 steps — payroll will be ready to run.
              </p>
              <ol className="scp-guide-steps scp-guide-steps--dropdown">
                {setupGuideSteps.map((s, i, arr) => (
                  <li className="scp-guide-step" key={s.n}>
                    <div className="scp-guide-step-card">
                      <div className="scp-guide-step-top">
                        <span className="scp-guide-num">{s.n}</span>
                        <span className="scp-guide-step-icon"><s.Icon size={16} /></span>
                      </div>
                      <div className="scp-guide-step-title">{s.title}</div>
                      <div className="scp-guide-step-text">{s.text}</div>
                      <button
                        type="button"
                        className="scp-guide-step-cta"
                        onClick={() => {
                          s.onClick();
                          setGuideOpen(false);
                        }}
                      >
                        {s.cta} <ArrowRight size={13} />
                      </button>
                    </div>
                    {i < arr.length - 1 ? <span className="scp-guide-arrow" aria-hidden="true"><ArrowRight size={14} /></span> : null}
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
        <PayrollSetupProgress refreshKey={setupRefreshKey} dropdown />
      </div>

      <section className="scp-pipeline-strip" aria-label="Salary structure creation">
        <LayoutTemplate size={16} className="scp-pipeline-strip-icon" aria-hidden />
        <p className="scp-pipeline-strip-text">
          Build a salary structure from your components — allocation, formulas, and review.
        </p>
        <label className="scp-pipeline-strip-adv">
          <input
            type="checkbox"
            checked={advancedMode}
            onChange={(e) => {
              const on = e.target.checked;
              setAdvancedMode(on);
              writeAdvancedModeEnabled(on);
            }}
          />
          Advanced
        </label>
        <button
          type="button"
          className="scp-pipeline-strip-cta"
          disabled={!advancedMode}
          title={advancedMode ? "Start guided template creation" : "Enable Advanced to start"}
          onClick={() => {
            startSalaryTemplatePipeline();
            navigate(pipelineStepPath(PIPELINE_STEPS.template));
          }}
        >
          Start Salary Structure <ArrowRight size={16} />
        </button>
      </section>

      {/* ── 5. WARNING PANEL ──────────────────────────────────── */}
      {warnings.length > 0 ? (
        <div className="scp-warnings" id="scp-warnings">
          {warnings.map((w) => (
            <div key={w.id} className={`scp-warn scp-warn-${w.severity}`}>
              <span className="scp-warn-icon">
                {w.severity === "info" ? <Sparkles size={18} /> : <AlertTriangle size={18} />}
              </span>
              <div className="scp-warn-body">
                <div className="scp-warn-title">{w.title}</div>
                <div className="scp-warn-text">{w.body}</div>
              </div>
              {w.targets.length > 0 ? (
                <button type="button" className="bw-btn bw-btn-primary scp-warn-btn" onClick={() => assignFormula(w.targets[0])}>
                  {w.id === "missing-system" ? "Configure" : "Assign formula"}
                </button>
              ) : (
                <button type="button" className="bw-btn bw-btn-primary scp-warn-btn" onClick={openCreate}>
                  Create component
                </button>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <SkeletonTable />
      ) : !hasAnyComponents && statusFilter === "active" && !search ? (
        /* ── 7. EMPTY STATE ── */
        <>
          <EmptyState
            onCreate={openCreate}
            onSeed={seedCatalog}
            seeding={seeding}
          />
        </>
      ) : (
        <>
          {/* ── 3. COMPONENT TABLE ── */}
          <section className="scp-card scp-card--table">
            <div className="scp-toolbar scp-toolbar--top">
              <div className="scp-search">
                <Search size={15} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by code or name…" aria-label="Search components" />
              </div>
              <select className="bw-select scp-mini-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All statuses</option>
              </select>
              <select className="bw-select scp-mini-select" value={formulaFilter} onChange={(e) => setFormulaFilter(e.target.value)} aria-label="Formula filter">
                <option value="all">All formulas</option>
                <option value="required">Needs formula</option>
                <option value="missing">Formula missing</option>
              </select>
              <button type="button" className="bw-btn bw-btn-secondary scp-refresh-btn" onClick={load} title="Refresh list">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>

            {/* bulk bar */}
            {selectedCount > 0 ? (
              <div className="scp-bulkbar">
                <span>{selectedCount} selected</span>
                <div className="scp-bulkbar-actions">
                  <button type="button" className="bw-btn bw-btn-secondary" onClick={() => setSelected(new Set())}>Clear</button>
                  <button type="button" className="bw-btn scp-btn-danger" onClick={() => setBulkConfirm(true)}>
                    <Archive size={14} /> Archive selected
                  </button>
                </div>
              </div>
            ) : null}

            <SplitComponentsTable
              title="Earnings"
              hint="Basic, HRA, allowances, and pre-tax benefits — these increase gross salary."
              tone="earning"
              count={earningsRows.length}
              rows={earningsRows}
              selected={selected}
              onToggleRow={toggleRow}
              onEdit={openEdit}
              onDuplicate={duplicate}
              onArchive={setArchiveTarget}
              onRestore={restore}
              onAssign={assignFormula}
              SortHead={SortHead}
            />
            <SplitComponentsTable
              title="Deductions"
              hint="PF, ESI, PT, TDS, loans, and other post-tax deductions — these reduce net pay."
              tone="deduction"
              count={deductionsRows.length}
              rows={deductionsRows}
              selected={selected}
              onToggleRow={toggleRow}
              onEdit={openEdit}
              onDuplicate={duplicate}
              onArchive={setArchiveTarget}
              onRestore={restore}
              onAssign={assignFormula}
              SortHead={SortHead}
            />
          </section>
        </>
      )}

      <div className="scp-guided-nav" role="navigation" aria-label="Payroll setup navigation">
        {prevSetupTab ? (
          <button type="button" className="bw-btn bw-btn-secondary" onClick={() => navigate(prevSetupTab.to)}>
            <ChevronLeft size={15} /> Back
          </button>
        ) : <span />}
        <button
          type="button"
          className="bw-btn bw-btn-primary"
          onClick={openSaveReview}
          disabled={nextLoading || !hasAnyComponents}
        >
          {nextLoading ? "Saving…" : "Save & continue →"}
        </button>
      </div>

      <SaveComponentsReviewModal
        open={saveReviewOpen}
        loading={nextLoading}
        componentsReady={componentsReady}
        earningsConfigured={earningsConfigured}
        deductionsConfigured={deductionsConfigured}
        overtimeRow={overtimeRow}
        advanceRow={advanceRow}
        activeEarnings={activeEarnings}
        activeDeductions={activeDeductions}
        onCancel={() => !nextLoading && setSaveReviewOpen(false)}
        onConfirm={confirmSaveAndContinue}
      />

      <ConfirmDialog
        open={Boolean(archiveTarget)}
        title={componentCodeOf(archiveTarget) === "ADVANCE_TAKEN" ? "Deactivate Advance Taken?" : "Archive this component?"}
        message={
          componentCodeOf(archiveTarget) === "ADVANCE_TAKEN"
            ? "Advance Taken will be turned off for the organisation. Employee advance entry is hidden, and pending advances are not deducted until you activate it again. Advances already applied to payroll stay in history."
            : archiveTarget
              ? `"${archiveTarget.name}" will be hidden from new payroll assignments. Existing templates may still reference it, and you can restore it anytime.`
              : ""
        }
        confirmLabel={componentCodeOf(archiveTarget) === "ADVANCE_TAKEN" ? "Deactivate" : "Archive"}
        cancelLabel="Cancel"
        danger
        loading={archiveLoading}
        onCancel={() => !archiveLoading && setArchiveTarget(null)}
        onConfirm={confirmArchive}
      />
      <ConfirmDialog
        open={bulkConfirm}
        title={`Archive ${selectedCount} component${selectedCount !== 1 ? "s" : ""}?`}
        message="Selected components will be hidden from new payroll assignments. You can restore them later from the Inactive filter."
        confirmLabel="Archive"
        cancelLabel="Cancel"
        danger
        loading={bulkLoading}
        onCancel={() => !bulkLoading && setBulkConfirm(false)}
        onConfirm={confirmBulkArchive}
      />
    </div>
  );
}

function SetupChecklistItem({ done, children }) {
  return (
    <li className={done ? "done" : ""}>
      {done ? <CheckCircle2 size={14} /> : <Clock size={14} />}
      {children}
    </li>
  );
}

function ActiveComponentGroup({ title, tone, items }) {
  return (
    <div className={`scp-save-group scp-save-group--${tone}`}>
      <div className="scp-save-group-head">
        <span className="scp-save-group-title">{title}</span>
        <span className="scp-save-group-count">{items.length} active</span>
      </div>
      {items.length === 0 ? (
        <p className="scp-save-group-empty">None active yet.</p>
      ) : (
        <ul className="scp-save-chips">
          {items.map((r) => (
            <li key={r.component_id}>
              <span className="scp-code-chip">{r.code}</span>
              {r.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SaveComponentsReviewModal({
  open,
  loading,
  componentsReady,
  earningsConfigured,
  deductionsConfigured,
  overtimeRow,
  advanceRow,
  activeEarnings,
  activeDeductions,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  const dialog = (
    <div className="scp-save-backdrop" role="dialog" aria-modal="true" aria-labelledby="scp-save-title" onClick={onCancel}>
      <div className="scp-save-modal" onClick={(e) => e.stopPropagation()}>
        <div className="scp-save-head">
          <div>
            <h2 id="scp-save-title" className="scp-save-title">Save salary components</h2>
            <p className="scp-save-sub">
              {componentsReady
                ? "These active components will be used in payroll and salary structures."
                : "Review setup status and active components before continuing."}
            </p>
          </div>
          <button type="button" className="scp-save-close" onClick={onCancel} disabled={loading} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <ul className="scp-complete-list scp-save-checklist">
          <SetupChecklistItem done={earningsConfigured}>Earnings configured</SetupChecklistItem>
          <SetupChecklistItem done={deductionsConfigured}>Deductions configured</SetupChecklistItem>
          <SetupChecklistItem done={Boolean(overtimeRow)}>Overtime synced with Extra Work &amp; OT</SetupChecklistItem>
          <SetupChecklistItem done={Boolean(advanceRow)}>Advance Taken available</SetupChecklistItem>
        </ul>

        <div className="scp-save-active">
          <ActiveComponentGroup title="Earnings" tone="earning" items={activeEarnings} />
          <ActiveComponentGroup title="Deductions" tone="deduction" items={activeDeductions} />
        </div>

        <div className="scp-save-actions">
          <button type="button" className="bw-btn bw-btn-secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button type="button" className="bw-btn bw-btn-primary" onClick={onConfirm} disabled={loading}>
            {loading ? "Saving…" : "Save & continue to structures"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

/* ── Split table section (earnings / deductions) ───────────── */
function SplitComponentsTable({
  title,
  hint,
  tone,
  count,
  rows,
  selected,
  onToggleRow,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onAssign,
  SortHead,
}) {
  return (
    <div className={`scp-split-section scp-split-${tone}`}>
      <div className="scp-split-head">
        <div>
          <h3 className="scp-split-title">
            {title} <span className="scp-split-count">({count})</span>
          </h3>
          <p className="scp-split-hint">{hint}</p>
        </div>
      </div>
      <div className="scp-table-wrap">
        <table className="scp-table">
          <thead>
            <tr>
              <th className="scp-checkcell" aria-hidden />
              <SortHead k="name">Component</SortHead>
              <SortHead k="code">Code</SortHead>
              <SortHead k="calc">Calculation</SortHead>
              <th>Taxable</th>
              <th>Active</th>
              <th>Formula</th>
              <SortHead k="updated">Updated</SortHead>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ComponentRow
                key={r.component_id}
                row={r}
                selected={selected}
                onToggleRow={onToggleRow}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onArchive={onArchive}
                onRestore={onRestore}
                onAssign={onAssign}
                hideCategory
              />
            ))}
            {rows.length === 0 ? (
              <tr>
                <td className="scp-table-empty" colSpan={9}>
                  No {title.toLowerCase()} yet — use Add Component above to create one.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComponentRow({
  row: r,
  selected,
  onToggleRow,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onAssign,
  hideCategory = false,
}) {
  const inactive = r.is_active === false;
  const taxable = r._cat === "earning" || r.meta?.pre_tax_benefit !== true;
  const variable = isVariablePayrollComponent(r);
  const vmeta = variableComponentMeta(r);
  const Icon = componentCodeOf(r) === "ADVANCE_TAKEN" ? TrendingDown : Zap;
  return (
    <tr className={`${selected.has(r.component_id) ? "selected" : ""} ${inactive ? "inactive" : ""} ${variable ? "scp-row-variable" : ""}`}>
      <td className="scp-checkcell">
        <input
          type="checkbox"
          checked={selected.has(r.component_id)}
          onChange={() => onToggleRow(r.component_id)}
          aria-label={`Select ${r.name}`}
          disabled={variable}
        />
      </td>
      <td>
        <div className="scp-name">
          {variable ? <span className="scp-var-icon" aria-hidden="true"><Icon size={14} /></span> : null}
          {r.name}
        </div>
        <div className="scp-name-sub">
          {hideCategory ? <CategoryBadge category={r._cat} /> : null}
          {variable ? (
            <span className="scp-pill scp-calc-variable" title={vmeta?.help || ""}>
              {vmeta?.badge || "Variable"}
              <Info size={11} />
            </span>
          ) : null}
          {componentCodeOf(r) === "ADVANCE_TAKEN" ? (
            <span>{r.description || "Salary advance recovered through payroll."}</span>
          ) : null}
          {isOvertimePayrollComponent(r) && r.meta?.extra_work_policy?.method_label ? (
            <span className="scp-pill scp-ot-sync" title="One overtime earning. Extra-time OT pay is included here.">
              {r.meta.extra_work_policy.method_label}
            </span>
          ) : null}
          {!hideCategory && !variable && r.description ? r.description : null}
        </div>
      </td>
      <td><span className="scp-code-chip">{r.code}</span></td>
      {!hideCategory ? <td><CategoryBadge category={r._cat} /></td> : null}
      <td><CalcBadge calc={r.calculation_type} row={r} /></td>
      <td>{taxable ? <span className="scp-pill scp-status-ok">Yes</span> : <span className="scp-pill scp-status-muted">No</span>}</td>
      <td>{inactive ? <span className="scp-pill scp-status-muted">Inactive</span> : <span className="scp-pill scp-status-ok">Active</span>}</td>
      <td><FormulaBadge row={r} /></td>
      <td className="scp-muted">{fmtDate(r.updated_at)}</td>
      <td className="text-right">
        <RowActions
          row={r}
          onEdit={() => onEdit(r)}
          onDuplicate={() => onDuplicate(r)}
          onArchive={() => onArchive(r)}
          onRestore={() => onRestore(r)}
          onAssign={() => onAssign(r)}
        />
      </td>
    </tr>
  );
}

/* ── Row actions popover ───────────────────────────────────── */
function RowActions({ row, onEdit, onDuplicate, onArchive, onRestore, onAssign }) {
  const s = formulaStatus(row);
  const inactive = row.is_active === false;
  return (
    <div className="scp-row-actions">
      {!s.ready ? (
        <button type="button" className="scp-act scp-act-accent" title="Assign formula" onClick={onAssign}>
          <Sparkles size={15} />
        </button>
      ) : null}
      <button type="button" className="scp-act" title="Edit" onClick={onEdit}><Pencil size={15} /></button>
      {isVariablePayrollComponent(row) ? null : (
        <button type="button" className="scp-act" title="Duplicate" onClick={onDuplicate}><Copy size={15} /></button>
      )}
      {inactive ? (
        isVariablePayrollComponent(row) ? (
          <button type="button" className="scp-act scp-act-ok scp-act-label" onClick={onRestore}>
            <RefreshCw size={14} />
            Activate
          </button>
        ) : (
          <button type="button" className="scp-act scp-act-ok" title="Restore" onClick={onRestore}><RefreshCw size={15} /></button>
        )
      ) : isDeletable(row) || componentCodeOf(row) === "ADVANCE_TAKEN" ? (
        <button type="button" className="scp-act scp-act-danger" title={componentCodeOf(row) === "ADVANCE_TAKEN" ? "Deactivate" : "Archive"} onClick={onArchive}><Archive size={15} /></button>
      ) : null}
    </div>
  );
}

/* ── Loading skeleton ──────────────────────────────────────── */
function SkeletonTable() {
  return (
    <section className="scp-card">
      <div className="scp-skel scp-skel-row" style={{ width: "40%" }} />
      <div className="scp-skel-table">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="scp-skel scp-skel-line" key={i} />
        ))}
      </div>
    </section>
  );
}

/* ── Empty state ───────────────────────────────────────────── */
function EmptyState({ onCreate, onSeed, seeding }) {
  return (
    <section className="scp-card scp-empty">
      <div className="scp-empty-art" aria-hidden="true">
        <Layers size={40} />
      </div>
      <h2 className="scp-empty-title">Choose the components your organisation uses</h2>
      <p className="scp-empty-text">
        This page shows only components you select or create — default catalog items are hidden until you choose them,
        so payroll setup stays clear and accurate.
      </p>
      <div className="scp-empty-actions">
        <button type="button" className="bw-btn bw-btn-primary scp-add-btn" onClick={onCreate}>
          <Plus size={16} /> Create component
        </button>
        <button type="button" className="bw-btn bw-btn-secondary" onClick={onSeed} disabled={seeding}>
          <Database size={15} /> {seeding ? "Loading…" : "Get default components"}
        </button>
      </div>
    </section>
  );
}
